import { useState } from 'react'
import { Link } from '@inertiajs/react'
import { ArrowRight, CircleCheck } from 'lucide-react'
import { Button } from '@/components/ui/button'
import ProjectSummaryTask from '@/frontend/features/Fde/ProjectSummaryTask'
import ProjectPlanBrief from '@/frontend/features/Fde/ProjectPlanBrief'
import ObjectiveSummary from '@/frontend/features/ProjectObjectives/Summary'
import type { Objective, ObjectiveCopy } from '@/frontend/features/ProjectObjectives/types'
import ProjectPlanDeliveries from '@/frontend/features/Fde/ProjectPlanDeliveries'
import ProjectPlanCheckpoint from '@/frontend/features/Fde/ProjectPlanCheckpoint'
import ProjectManagementHistory from '@/frontend/features/Fde/ProjectManagementHistory'
import type { ManagementHistoryCopy } from '@/frontend/features/Fde/projectManagementHistoryTypes'
import ProjectPlanEditor from '@/frontend/features/Fde/ProjectPlanEditor'
import { fdeProjectPath, fdeRoutes } from '@/frontend/features/Fde/routes'
import type { TaskSurfaceProps } from '@/frontend/features/Fde/ProjectTaskCanvas'
import type {
  ProjectWorkSummaryData,
  ProjectTaskFilter,
} from '@/frontend/features/Fde/managementTypes'
import type {
  ProjectPlan,
  PlanEditor,
  ProjectPlanFiles,
} from '@/frontend/features/Fde/projectPlanTypes'

interface Props extends TaskSurfaceProps {
  objectives?: Objective[]
  objectiveCopy?: ObjectiveCopy
  summary: ProjectWorkSummaryData
  plan: ProjectPlan
  fileDirectory?: ProjectPlanFiles
  projectId: string
  today: string
  canViewFiles: boolean
  historyCopy: ManagementHistoryCopy
  onScope: (scope: ProjectTaskFilter) => void
}

export default function ProjectWorkSummary(props: Props) {
  const [editor, setEditor] = useState<PlanEditor | null>(null)
  const { plan, summary, tasks, copy } = props
  const words = copy.management
  const attentionCount = summary.attention_count + (plan.decision_count ?? 0)
  const attention = summary.attention_task_ids.flatMap((id) => {
    const task = tasks.find((item) => item.id === id)
    return task ? [task] : []
  })
  return (
    <div className="h-full overflow-auto px-4 py-4 lg:px-6 lg:py-5">
      <div className="mx-auto max-w-5xl space-y-5">
        <ProjectPlanBrief
          plan={plan}
          copy={copy.project_plan}
          canManage={props.canManage}
          onEdit={() => setEditor({ mode: 'direction' })}
        />
        <ProjectPlanCheckpoint plan={plan} copy={copy.project_plan} projectId={props.projectId} canManage={props.canManage} onEdit={() => setEditor({ mode: 'checkpoint' })}
          historyAction={<ProjectManagementHistory projectId={props.projectId} copy={props.historyCopy}
            revisionKey={[plan.lock_version, ...(props.objectives ?? []).map(item => `${item.id}:${item.lock_version}`)].join('|')} />} />
        {props.objectiveCopy && <ObjectiveSummary objectives={props.objectives || []} projectId={props.projectId} canManage={props.canManage} copy={props.objectiveCopy} />}
        <div className="grid items-start gap-6 lg:grid-cols-[minmax(0,1.5fr)_minmax(0,1fr)]">
          <ProjectPlanDeliveries
            plan={plan}
            tasks={tasks}
            copy={copy}
            projectId={props.projectId}
            today={props.today}
            canManage={props.canManage}
            onEdit={(delivery) => setEditor({ mode: 'delivery', delivery })}
          />
          <section aria-label={words.summary_attention} className="min-w-0">
            <div className="mb-3 flex min-h-7 items-center justify-between gap-2">
              <h2 className="text-sm font-semibold">
                {words.summary_attention}
                {attentionCount > 0 && (
                  <span className="ml-2 rounded bg-amber-500/10 px-1.5 py-0.5 text-[11px] font-normal text-amber-700 dark:text-amber-400">
                    {attentionCount}
                  </span>
                )}
              </h2>
              {summary.attention_count > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 gap-1 px-1 text-xs text-muted-foreground"
                  onClick={() => props.onScope('attention')}
                >
                  {copy.project_plan.review_tasks}
                  <ArrowRight className="size-3" />
                </Button>
              )}
            </div>
            <div className="divide-y divide-border overflow-hidden rounded-lg border border-border bg-background">
              {attention.map((task) => (
                <ProjectSummaryTask
                  {...props}
                  key={task.id}
                  task={task}
                  variant="attention"
                  compact
                  sourcePaths={[]}
                />
              ))}
              {plan.decisions.map((decision) => (
                <Link
                  key={decision.id}
                  href={fdeProjectPath(fdeRoutes.outcomes, props.projectId)}
                  className="block px-4 py-3 hover:bg-muted/30"
                >
                  <span className="text-[11px] text-muted-foreground">
                    {copy.project_plan.decision_pending}
                  </span>
                  <p className="mt-1 line-clamp-2 text-[13px] leading-5" title={decision.statement}>{decision.statement}</p>
                </Link>
              ))}
              {!attention.length && !plan.decisions.length && (
                <p className="flex items-start gap-2 px-4 py-6 text-xs leading-5 text-muted-foreground">
                  <CircleCheck className="mt-0.5 size-3.5 shrink-0" />
                  {words.summary_no_attention}
                </p>
              )}
            </div>
          </section>
        </div>
      </div>
      {editor && (
        <ProjectPlanEditor
          editor={editor}
          plan={plan}
          projectId={props.projectId}
          copy={copy}
          tasks={tasks}
          onClose={() => setEditor(null)}
          fileDirectory={props.fileDirectory}
        />
      )}
    </div>
  )
}
